import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type Locale } from '@/i18n';
import { hasSupabase, getSupabase } from '@/services/supabaseService';

/**
 * The product ships dark-only: there is no theme switcher and no light
 * palette to fall back to. Exposed as a store getter rather than a literal
 * in every consumer so widgets that must mirror the app chrome (the hCaptcha
 * iframe, KDS components) keep reading from a single source of truth.
 */
const FIXED_THEME = 'dark' as const;

// Storage key kept as `quorum.*` for backwards-compat with existing users.
// The UI brand is `korum studio` (see `app.name` in i18n) — this string
// is never rendered. Renaming would silently wipe user preferences.
const LOCALE_KEY = 'quorum.locale';

const isLocale = (value: string): value is Locale =>
  (SUPPORTED_LOCALES as readonly string[]).includes(value);

export const useThemeStore = defineStore('ui', () => {
  const locale = ref<Locale>(DEFAULT_LOCALE);

  // D1: when truthy, every locale change is mirrored to members.*.
  // Set by `hydrateFromMember` once we know who the caller is.
  const persistUserId = ref<string | null>(null);

  const resolvedTheme = computed<typeof FIXED_THEME>(() => FIXED_THEME);

  const applyToDocument = () => {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('data-theme', FIXED_THEME);
    document.documentElement.setAttribute('lang', locale.value);
  };

  const persistRemote = async (patch: { locale_preference?: Locale }) => {
    if (!persistUserId.value || !hasSupabase()) return;
    try {
      const supabase = getSupabase();
      await supabase.from('members').update(patch).eq('user_id', persistUserId.value);
    } catch (err) {
      // Persistence is best-effort. Fall back silently to localStorage.
      if (typeof console !== 'undefined') {
        console.warn('[ui] members preference persist failed', err);
      }
    }
  };

  const setLocale = (next: Locale) => {
    locale.value = next;
    if (typeof localStorage !== 'undefined') localStorage.setItem(LOCALE_KEY, next);
    applyToDocument();
    void persistRemote({ locale_preference: next });
  };

  const restoreFromStorage = () => {
    if (typeof localStorage !== 'undefined') {
      const storedLocale = localStorage.getItem(LOCALE_KEY);
      if (storedLocale && isLocale(storedLocale)) {
        locale.value = storedLocale;
      }
    }
    applyToDocument();
  };

  /**
   * D1 — Hydrate from `members.locale_preference`. DB wins on conflict (read
   * at sign-in is the source of truth). Local mirror is updated so the value
   * survives a future anonymous reload. `members.theme_preference` is ignored:
   * the app is dark-only.
   */
  const hydrateFromMember = async (userId: string) => {
    persistUserId.value = userId;
    if (!hasSupabase()) {
      applyToDocument();
      return;
    }
    try {
      const supabase = getSupabase();
      const { data } = await supabase
        .from('members')
        .select('locale_preference')
        .eq('user_id', userId)
        .single();
      if (!data) {
        applyToDocument();
        return;
      }
      if (typeof data.locale_preference === 'string' && isLocale(data.locale_preference)) {
        locale.value = data.locale_preference;
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(LOCALE_KEY, data.locale_preference);
        }
      }
    } catch (err) {
      if (typeof console !== 'undefined') {
        console.warn('[ui] members preference hydration failed', err);
      }
    } finally {
      applyToDocument();
    }
  };

  const clearRemotePersistence = () => {
    persistUserId.value = null;
  };

  watch(locale, applyToDocument);

  return {
    locale,
    resolvedTheme,
    setLocale,
    restoreFromStorage,
    hydrateFromMember,
    clearRemotePersistence
  };
});
